import { useState } from "react";
import { Link } from "react-router";
import { FolderPlus, Link2, Unlink } from "lucide-react";
import {
  useCreateCollectionFromRecordMutation,
  useSetCollectionTitleRecordMutation,
} from "@/api/api";
import { useMyCollections } from "@/api/hooks";
import { collectionPath } from "@/common/urls";
import { globalReloader } from "@/providers/dbRecordsLoader";
import { Button } from "../button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../command";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";
import { Field, FieldLabel } from "../field";

interface RecordCollectionLinkProps {
  /** Undefined until the record has been saved once. */
  recordID: string | undefined;
  /** The collection this record titles, if any. */
  titleCollectionID: string | null;
  /**
   * Controlled from the form, because linking has an effect the form has to
   * mirror: the server drops the record's membership of the collection it now
   * titles, and the collections picker up there would otherwise keep showing it.
   */
  onChange: (collectionID: string | null) => void;
}

/**
 * Turning a record into a collection, and undoing it.
 *
 * Two ways in, because both situations are real: **Make a collection** when the
 * collection does not exist yet, and **Link an existing one** when the user
 * already built "Second Punic War" as a collection and only now wants the record
 * of that name to head it.
 *
 * Both live on the record rather than in the collection settings sheet, and the
 * asymmetry is deliberate. From here the picker is over *collections*, which are
 * already fully loaded client-side; from the collection it would be a picker over
 * every record the user owns, which needs a search endpoint that does not exist.
 * So the collection side is read-and-unlink only.
 *
 * Unlinking leaves both halves standing — the collection keeps its records, the
 * record keeps its own. Only the link goes.
 */
export default function RecordCollectionLink({
  recordID,
  titleCollectionID,
  onChange,
}: RecordCollectionLinkProps) {
  const collections = useMyCollections();
  const [createCollection, createState] =
    useCreateCollectionFromRecordMutation();
  // One mutation covers linking and unlinking: `record_id: null` clears.
  const [setTitleRecord, linkState] = useSetCollectionTitleRecordMutation();
  const [error, setError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Nothing to link until the record exists: every endpoint addresses it by id.
  if (!recordID) return null;

  const collection = collections.find((c) => c.id === titleCollectionID);
  const busy = createState.isLoading || linkState.isLoading;

  // A record titles at most one collection and a collection has at most one
  // title record, so anything already spoken for is not on offer — the API
  // answers 409 for those, and an option that can only fail is not an option.
  const available = collections.filter((c) => !c.title_record_id);

  // The timeline's records come from DBRecordsLoader, not RTK Query, so tag
  // invalidation alone would leave the list showing records that should now be
  // folded away (or the other way round) until the next navigation.
  const handleCreate = async () => {
    setError(null);
    try {
      const created = await createCollection(recordID).unwrap();
      onChange(created.id);
      globalReloader.reload();
    } catch {
      // The likely causes are both worth naming: the collection quota, and a
      // second click landing before the first response.
      setError("Could not create the collection. You may be out of slots.");
    }
  };

  const handleLink = async (targetID: string) => {
    setPickerOpen(false);
    setError(null);
    try {
      await setTitleRecord({ collectionID: targetID, recordID }).unwrap();
      onChange(targetID);
      globalReloader.reload();
    } catch {
      setError("Could not link that collection.");
    }
  };

  const handleUnlink = async () => {
    if (!titleCollectionID) return;
    setError(null);
    try {
      await setTitleRecord({
        collectionID: titleCollectionID,
        recordID: null,
      }).unwrap();
      onChange(null);
      globalReloader.reload();
    } catch {
      setError("Could not unlink the collection.");
    }
  };

  return (
    <Field>
      <FieldLabel className="uppercase text-[10.5px] tracking-[.08em] font-bold text-muted-foreground">
        Collection of its own
      </FieldLabel>

      {titleCollectionID ? (
        <div className="flex items-center gap-2 rounded-[10px] border border-border bg-secondary px-3 py-2">
          <Link
            to={collection ? collectionPath(collection) : "/"}
            className="min-w-0 flex-1 truncate text-[13px] text-foreground hover:underline"
          >
            {collection?.title ?? "Open collection"}
          </Link>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={handleUnlink}
            title="Keep both, drop the link between them"
          >
            <Unlink />
            Unlink
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-1.5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={handleCreate}
            >
              <FolderPlus />
              {createState.isLoading ? "Creating…" : "Make a collection"}
            </Button>

            <Popover open={pickerOpen} onOpenChange={setPickerOpen} modal>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  role="combobox"
                  aria-expanded={pickerOpen}
                  disabled={busy || available.length === 0}
                  title={
                    available.length === 0
                      ? "Every collection already has a title record"
                      : "Head a collection you already have"
                  }
                >
                  <Link2 />
                  Link existing
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[260px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search collections…" />
                  <CommandList>
                    <CommandEmpty>No collection found.</CommandEmpty>
                    <CommandGroup>
                      {available.map((c) => (
                        <CommandItem
                          key={c.id}
                          // Searched by title, acted on by id: two collections
                          // may share a title, and only the id is unambiguous.
                          value={`${c.title} ${c.id}`}
                          onSelect={() => handleLink(c.id)}
                        >
                          <span className="truncate">{c.title}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <p className="text-[11px] leading-[1.5] text-muted-foreground">
            Gives this record a collection to group the events inside it.
            Elsewhere on the timeline they fold into this record instead of
            listing beside it. Linking a collection this record is filed in also
            takes it out of that collection — a title record never belongs to
            what it titles.
          </p>
        </div>
      )}

      {error && <p className="text-[11px] text-destructive">{error}</p>}
    </Field>
  );
}
